import React from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "../../../../../ui/components/Dialog";
import { Button } from "../../../../../ui/components/Button";
import { Badge } from "../../../../../ui/components/Badge";
import { IconButton } from "../../../../../ui/components/IconButton";
import { FeatherX, FeatherExternalLink } from "@subframe/core";
import type { DocumentChangeAuditEntity } from "../../../domain/entities/DocumentChangeAuditEntity";
import { formatDateDDMMYYYY } from "../../../../../shared/utils/formatters";
import {
  getPostedStatusLabel,
  getStoredAccountingBackend,
} from "../../../../../config/accounting";
import { DocumentChangeDetailPanel } from "./DocumentChangeDetailPanel";

interface DocumentChangeModalProps {
  audit: DocumentChangeAuditEntity | null;
  open: boolean;
  onClose: () => void;
}

const formatDate = (value: string | null): string => {
  if (!value) return "—";
  return formatDateDDMMYYYY(value) || value;
};

export const DocumentChangeModal: React.FC<DocumentChangeModalProps> = ({
  audit,
  open,
  onClose,
}) => {
  const navigate = useNavigate();

  if (!audit) return null;

  const isBill = audit.entityType === "bill";
  const partyLabel = isBill ? "Vendor" : "Customer";
  const postedLabel = getPostedStatusLabel(getStoredAccountingBackend());

  const handleOpenDocument = () => {
    navigate(isBill ? "/bills/review" : "/invoices/review", {
      state: {
        documentId: String(audit.entityId),
        returnTo: "admin-reports",
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <Dialog.Content className="w-[720px] max-w-[92vw]">
        <div className="flex w-full flex-col items-start">
          {/* Header */}
          <div className="flex w-full items-start justify-between gap-4 border-b border-solid border-neutral-border px-6 py-4">
            <div className="flex flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <Badge variant={isBill ? "warning" : "brand"}>
                  {isBill ? "Bill" : "Invoice"}
                </Badge>
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {audit.documentNumber || `#${audit.entityId}`}
                </span>
              </div>
              <span className="text-body font-body text-subtext-color">
                {partyLabel}: {audit.partyName || "—"}
              </span>
            </div>
            <IconButton
              variant="neutral-tertiary"
              icon={<FeatherX />}
              onClick={onClose}
            />
          </div>

          {/* Meta row */}
          <div className="flex w-full flex-wrap items-center gap-x-8 gap-y-3 border-b border-solid border-neutral-border px-6 py-4">
            <div className="flex flex-col gap-0.5">
              <span className="text-caption font-caption text-subtext-color">
                Uploaded
              </span>
              <span className="text-body font-body text-default-font">
                {formatDate(audit.uploadedAt)}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-caption font-caption text-subtext-color">
                {postedLabel}
              </span>
              <span className="text-body font-body text-default-font">
                {formatDate(audit.postedToZohoDate)}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-caption font-caption text-subtext-color">
                Posted by
              </span>
              <span className="text-body font-body text-default-font">
                {audit.userName || audit.userEmail || "—"}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-caption font-caption text-subtext-color">
                Status
              </span>
              {audit.hasMeaningfulChanges ? (
                <Badge variant="warning">
                  {audit.changeCount} change
                  {audit.changeCount === 1 ? "" : "s"}
                </Badge>
              ) : (
                <Badge variant="success">No edits</Badge>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="flex w-full flex-col items-start gap-4 px-6 py-5">
            <DocumentChangeDetailPanel audit={audit} />
          </div>

          {/* Footer */}
          <div className="flex w-full items-center justify-end gap-2 border-t border-solid border-neutral-border px-6 py-4">
            <Button variant="neutral-secondary" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="brand-primary"
              iconRight={<FeatherExternalLink />}
              onClick={handleOpenDocument}
            >
              Open {isBill ? "bill" : "invoice"}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
